import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { KanbanSquare, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

import { kanbanApiService } from '../../services/kanbanApi';
import { KanbanCardTypeBadge } from './KanbanCardTypeBadge';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';
import type { KanbanCardLink, KanbanCardLinkType } from '../../types/kanban';

interface KanbanLinkedCardsSectionProps {
  /** The link_type to search for (e.g. 'incident', 'partnership', 'build', 'player', 'appeal') */
  linkType: KanbanCardLinkType;
  /** The ID of the entity to find linked cards for */
  targetId: string;
  /** Optional title override */
  title?: string;
  /** Optional className */
  className?: string;
}

interface EnrichedLink extends KanbanCardLink {
  card_title?: string;
  card_type?: string;
  card_priority?: number;
  board_title?: string;
  board_id?: string;
}

/**
 * Reusable section component that shows kanban cards linked to ANY ARGUS entity.
 * Drop this into any detail view (incidents, partnerships, builds, appeals, player pages)
 * to show bi-directional kanban integration via the reverse link lookup API.
 */
export const KanbanLinkedCardsSection = ({
  linkType,
  targetId,
  title = 'Kanban Cards',
  className,
}: KanbanLinkedCardsSectionProps) => {
  const [links, setLinks] = useState<EnrichedLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    setLoading(true);

    kanbanApiService.reverseCardLinkLookup(linkType, targetId)
      .then(async (rawLinks) => {
        if (cancelled) return;
        // Enrich links with card data where possible
        const enriched: EnrichedLink[] = await Promise.all(
          rawLinks.map(async (link) => {
            try {
              const card = await kanbanApiService.getCard(link.card_id);
              return {
                ...link,
                card_title: card.title,
                card_type: card.card_type,
                card_priority: card.priority,
                board_id: card.board_id,
              };
            } catch {
              return { ...link };
            }
          })
        );
        if (!cancelled) setLinks(enriched);
      })
      .catch(() => {
        if (!cancelled) setLinks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [linkType, targetId]);

  // Don't render at all if no links and not loading
  if (!loading && links.length === 0) return null;

  return (
    <div className={cn('glass-card p-4', className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left gap-3"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <KanbanSquare className="w-4 h-4 text-accent-300 shrink-0" />
          <h3 className="text-sm font-semibold text-white truncate">{title}</h3>
          {!loading && (
            <span className="badge badge-neutral">{links.length}</span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-glass-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-glass-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {loading ? (
            <div className="space-y-2" aria-busy="true" aria-label="Loading linked cards">
              <SkeletonLoader className="h-12 w-full" />
              <SkeletonLoader className="h-12 w-full" />
            </div>
          ) : (
            links.map((link) => (
              <Link
                key={link.id}
                to={`/workspaces/${link.board_id || ''}`}
                className="subcard flex items-center gap-3 p-2.5 hover:border-glass-200/25 transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {link.card_type && (
                      <KanbanCardTypeBadge type={link.card_type as import('../../types/kanban').KanbanCardType} showLabel={false} />
                    )}
                    <span className="text-sm text-white font-medium truncate group-hover:text-accent-300 transition-colors">
                      {link.card_title || link.card_id}
                    </span>
                    {link.card_priority && link.card_priority !== 5 && (
                      <KanbanPriorityBadge priority={link.card_priority} />
                    )}
                  </div>
                  {link.notes && (
                    <p className="text-xs text-glass-400 mt-0.5 truncate">{link.notes}</p>
                  )}
                  <p className="text-[10px] text-glass-500 mt-0.5 tabular-nums">
                    Linked {formatDistanceToNow(new Date(link.linked_at), { addSuffix: true })}
                  </p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-glass-400 group-hover:text-accent-300 flex-shrink-0" />
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
};
